import {naturalSortByFunction} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import DashboardLayout, {BlockSpec} from 'analysis-tool/ui/components/DashboardLayout';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assertAndContinue} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DeprecatedDataFrameFragment} from 'data-frames/graphql/GetDataFrame';
import {numericAccessor, stringAccessor, stringListAccessor} from 'data-frames/util/AccessorUtils';
import {createAccessors} from 'data-frames/util/createAccessors';
import resolveDataFrameValue, {DataFrameValueResolutionMode} from 'data-frames/util/resolveDataFrameValue';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import ErrorBanner from 'ui-library/banners/ErrorBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {Header1} from 'ui-library/Headers';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {
  GetParameterOptimizationOutputsQuery,
  GetParameterOptimizationOutputsQueryVariables,
} from './__generated/GetParameterOptimizationOutputsQuery';
import FeatureContributionBlock, {
  FEATURE_CONTRIBUTION_BLOCK_TITLE,
} from './modeling-dashboard/FeatureContributionBlock';
import FeatureImportanceBlock, {
  FEATURE_IMPORTANCE_BLOCK_TITLE,
} from './modeling-dashboard/FeatureImportanceBlock';
import LocalExplanationBlock, {
  LOCAL_EXPLANATION_BLOCK_TITLE,
} from './modeling-dashboard/LocalExplanationBlock';
import ModelMetricsTableBlock, {
  MODEL_METRICS_TABLE_BLOCK_TITLE,
} from './modeling-dashboard/ModelMetricsTableBlock';
import PredictedVsActualBlock, {
  PREDICTED_VS_ACTUAL_BLOCK_TITLE,
} from './modeling-dashboard/PredictedVsActualBlock';
import PredictionsAndLabelsBlock, {
  PREDICTIONS_AND_LABELS_BLOCK_TITLE,
} from './modeling-dashboard/PredictionsAndLabelsBlock';
import SearchRankingsTableBlock, {
  SEARCH_RANKINGS_BLOCK_TITLE,
} from './modeling-dashboard/SearchRankingsTableBlock';
import SelectedPredictionFeaturesBlock, {
  SELECTED_PREDICTION_FEATURES_TITLE,
} from './modeling-dashboard/SelectedPredictionFeaturesBlock';
import {
  ALL_DATA_COLUMNS_MAPPING,
  ALL_DATA_DATA_FRAME_NAME,
  ALL_DATA_VALIDATION_MAP,
  FEATURE_IMPORTANCE_COLUMNS_MAPPING,
  FEATURE_IMPORTANCE_DATA_FRAME_NAME,
  FEATURE_IMPORTANCE_VALIDATION_MAP,
  MODEL_EVALUATION_DATA_FRAME_NAME,
  SEARCH_RANKINGS_DATA_FRAME_NAME,
  SHAP_VALUES_COLUMNS_MAPPING,
  SHAP_VALUES_DATA_FRAME_NAME,
  SHAP_VALUES_VALIDATION_MAP,
} from './ParameterOptimizationConstants';

const BLOCK_HEIGHT = 400;
const BASE_BLOCK_SPEC: Omit<BlockSpec, 'key' | 'block'> = {
  // We need to set the min height of our blocks in block spec to give it an initial height and max height to
  // restrict the height of blocks that could otherwise be tall because of uncontrolled height like blocks containing
  // `DataFrameTable`s.
  minHeight: BLOCK_HEIGHT,
  maxHeight: BLOCK_HEIGHT,
};

const ALL_BLOCK_KEYS = [
  PREDICTED_VS_ACTUAL_BLOCK_TITLE,
  MODEL_METRICS_TABLE_BLOCK_TITLE,
  SEARCH_RANKINGS_BLOCK_TITLE,
  FEATURE_IMPORTANCE_BLOCK_TITLE,
  FEATURE_CONTRIBUTION_BLOCK_TITLE,
  PREDICTIONS_AND_LABELS_BLOCK_TITLE,
  LOCAL_EXPLANATION_BLOCK_TITLE,
  SELECTED_PREDICTION_FEATURES_TITLE,
] as const;
type AllBlockKeys = (typeof ALL_BLOCK_KEYS)[number];

export const GET_PARAMETER_OPTIMIZATION_MODELING_DASHBOARD_DATA_FRAMES_QUERY = gql`
  query GetParameterOptimizationOutputsQuery(
    $allDataId: StrictString!
    $featureImportanceId: StrictString!
    $modelEvaluationId: StrictString!
    $searchRankingsId: StrictString!
    $shapValuesId: StrictString!
  ) @skipAudit @engComponent(name: MACHINE_LEARNING) {
    allData: dataFrame(id: $allDataId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    featureImportance: dataFrame(id: $featureImportanceId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    modelEvaluation: dataFrame(id: $modelEvaluationId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    searchRankings: dataFrame(id: $searchRankingsId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
    shapValues: dataFrame(id: $shapValuesId) {
      ... on DataFrameNode {
        ...DataFrameGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${DeprecatedDataFrameFragment.fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/** @package */
export type LegacyParameterOptimizationModelingDashboardViewProps =
  MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD>;

const selectedDataPointIdGlobalState = makeUseGlobalClientState<string | null>(null);

interface DashboardDataFrames {
  allData: DataFrame;
  featureImportance: DataFrame;
  modelEvaluation: DataFrame;
  searchRankings: DataFrame;
  shapValues: DataFrame;
}

const expandedBlockState = makeUseGlobalClientState<AllBlockKeys | null>(null);

function LegacyParameterOptimizationModelingDashboardView({
  data: {allData, featureImportance, modelEvaluation, searchRankings, shapValues},
  step: {id: stepId},
}: MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD> & {
  data: DashboardDataFrames;
}): JSX.Element {
  const [expandedBlock, setExpandedBlock] = expandedBlockState.useGlobalClientState(stepId);

  const {accessors: featureImportanceAccessors, validatedLeafTypes: featureImportanceLeafTypes} =
    createAccessors(featureImportance, FEATURE_IMPORTANCE_COLUMNS_MAPPING, FEATURE_IMPORTANCE_VALIDATION_MAP);
  const {accessors: shapValuesAccessors, validatedLeafTypes: shapValueLeafTypes} = createAccessors(
    shapValues,
    SHAP_VALUES_COLUMNS_MAPPING,
    SHAP_VALUES_VALIDATION_MAP
  );
  const {accessors: allDataAccessors} = createAccessors(
    allData,
    ALL_DATA_COLUMNS_MAPPING,
    ALL_DATA_VALIDATION_MAP
  );

  const featureImportanceBaseEntitiesAccessor = stringListAccessor(featureImportanceAccessors.baseEntities);
  const featureImportanceBaseFeaturesAccessor = stringListAccessor(featureImportanceAccessors.baseFeatures);

  const shapValuesBaseEntitiesAccessor = stringListAccessor(shapValuesAccessors.baseEntities);
  const shapValuesBaseFeaturesAccessor = stringListAccessor(shapValuesAccessors.baseFeatures);

  const dataPointIdAccessor = stringAccessor(allDataAccessors.dataPointId);

  function resolveDataPointId(dataPointId: string): string {
    return String(
      // `shapValues` and `allData` could in theory have different `hydratedProperties`
      // (they are the same file on the pipelines backend), so we only want one of the data frame's
      // `hydratedProperties` so that they're resolved consistently in the UI incase they're ever different.
      resolveDataFrameValue(dataPointId, allData.hydratedProperties, {
        apiIdentifiedObjects: DataFrameValueResolutionMode.NAME,
        blobs: DataFrameValueResolutionMode.NONE,
      })
    );
  }

  const dataPointIds = naturalSortByFunction(
    allData.rows.map((row) => dataPointIdAccessor(row)),
    resolveDataPointId
  );

  const [maybeSelectedDataPointId, setSelectedDataPointId] =
    selectedDataPointIdGlobalState.useGlobalClientState(stepId);
  const selectedDataPointId = maybeSelectedDataPointId ?? dataPointIds[0];

  const blockSpecsMap: Record<AllBlockKeys, BlockSpec> = {
    [PREDICTED_VS_ACTUAL_BLOCK_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: PREDICTED_VS_ACTUAL_BLOCK_TITLE,
      block: (
        <PredictedVsActualBlock
          dashboardClientStateId={stepId}
          dataFrame={allData}
          columnsMapping={ALL_DATA_COLUMNS_MAPPING}
          isExpanded={expandedBlock === PREDICTED_VS_ACTUAL_BLOCK_TITLE}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? PREDICTED_VS_ACTUAL_BLOCK_TITLE : null);
          }}
        />
      ),
    },
    [MODEL_METRICS_TABLE_BLOCK_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: MODEL_METRICS_TABLE_BLOCK_TITLE,
      block: (
        <ModelMetricsTableBlock
          dashboardClientStateId={stepId}
          dataFrame={modelEvaluation}
          isExpanded={expandedBlock === MODEL_METRICS_TABLE_BLOCK_TITLE}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? MODEL_METRICS_TABLE_BLOCK_TITLE : null);
          }}
        />
      ),
    },
    [SEARCH_RANKINGS_BLOCK_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: SEARCH_RANKINGS_BLOCK_TITLE,
      block: (
        <SearchRankingsTableBlock
          dashboardClientStateId={stepId}
          dataFrame={searchRankings}
          isExpanded={expandedBlock === SEARCH_RANKINGS_BLOCK_TITLE}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? SEARCH_RANKINGS_BLOCK_TITLE : null);
          }}
        />
      ),
    },
    [FEATURE_IMPORTANCE_BLOCK_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: FEATURE_IMPORTANCE_BLOCK_TITLE,
      block: (
        <FeatureImportanceBlock
          dashboardClientStateId={stepId}
          dataFrame={featureImportance}
          baseEntitiesAccessor={featureImportanceBaseEntitiesAccessor}
          baseFeaturesAccessor={featureImportanceBaseFeaturesAccessor}
          importanceAccessor={numericAccessor(
            featureImportanceAccessors.importance,
            featureImportanceLeafTypes.importance
          )}
          isExpanded={expandedBlock === FEATURE_IMPORTANCE_BLOCK_TITLE}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? FEATURE_IMPORTANCE_BLOCK_TITLE : null);
          }}
        />
      ),
    },
    [FEATURE_CONTRIBUTION_BLOCK_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: FEATURE_CONTRIBUTION_BLOCK_TITLE,
      block: (
        <FeatureContributionBlock
          dashboardClientStateId={stepId}
          dataFrame={shapValues}
          columnsMapping={SHAP_VALUES_COLUMNS_MAPPING}
          isExpanded={expandedBlock === FEATURE_CONTRIBUTION_BLOCK_TITLE}
          baseEntitiesAccessor={shapValuesBaseEntitiesAccessor}
          baseFeaturesAccessor={shapValuesBaseFeaturesAccessor}
          featureValuesAccessor={shapValuesAccessors.featureValues}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? FEATURE_CONTRIBUTION_BLOCK_TITLE : null);
          }}
        />
      ),
    },
    [PREDICTIONS_AND_LABELS_BLOCK_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: PREDICTIONS_AND_LABELS_BLOCK_TITLE,
      block: (
        <PredictionsAndLabelsBlock
          dashboardClientStateId={stepId}
          allDataDataFrame={allData}
          columnsMapping={ALL_DATA_COLUMNS_MAPPING}
          dataPointIdAccessor={dataPointIdAccessor}
          isExpanded={expandedBlock === PREDICTIONS_AND_LABELS_BLOCK_TITLE}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? PREDICTIONS_AND_LABELS_BLOCK_TITLE : null);
          }}
          shapValuesDataFrame={shapValues}
        />
      ),
    },
    [LOCAL_EXPLANATION_BLOCK_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: LOCAL_EXPLANATION_BLOCK_TITLE,
      block: (
        <LocalExplanationBlock
          dashboardClientStateId={stepId}
          dataFrame={shapValues}
          columnsMapping={SHAP_VALUES_COLUMNS_MAPPING}
          isExpanded={expandedBlock === LOCAL_EXPLANATION_BLOCK_TITLE}
          baseEntitiesAccessor={shapValuesBaseEntitiesAccessor}
          baseFeaturesAccessor={shapValuesBaseFeaturesAccessor}
          dataPointIdAccessor={dataPointIdAccessor}
          resolveDataPointId={resolveDataPointId}
          shapValueAccessor={numericAccessor(shapValuesAccessors.shapValue, shapValueLeafTypes.shapValue)}
          selectedDataPointId={selectedDataPointId}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? LOCAL_EXPLANATION_BLOCK_TITLE : null);
          }}
        />
      ),
    },
    [SELECTED_PREDICTION_FEATURES_TITLE]: {
      ...BASE_BLOCK_SPEC,
      key: SELECTED_PREDICTION_FEATURES_TITLE,
      block: (
        <SelectedPredictionFeaturesBlock
          dashboardClientStateId={stepId}
          dataFrame={allData}
          featuresAccessor={allDataAccessors.features}
          predictedOutputAccessor={allDataAccessors.predictedOutput}
          dataPointIdAccessor={stringAccessor(allDataAccessors.dataPointId)}
          resolveDataPointId={resolveDataPointId}
          selectedDataPointId={selectedDataPointId}
          actualOutputAccessor={allDataAccessors.actualOutput}
          isExpanded={expandedBlock === SELECTED_PREDICTION_FEATURES_TITLE}
          onToggleExpand={(newIsExpanded) => {
            setExpandedBlock(newIsExpanded ? SELECTED_PREDICTION_FEATURES_TITLE : null);
          }}
        />
      ),
    },
  };

  const summarySection = (
    <>
      <Header1 className={css(styles.header, styles.firstHeader)}>Model summary</Header1>
      <DashboardLayout
        blockSpecs={[
          blockSpecsMap[PREDICTED_VS_ACTUAL_BLOCK_TITLE],
          blockSpecsMap[MODEL_METRICS_TABLE_BLOCK_TITLE],
          blockSpecsMap[SEARCH_RANKINGS_BLOCK_TITLE],
        ]}
      />
    </>
  );

  const parameterAnalysisSection = (
    <>
      <Header1 className={css(styles.header)}>Parameter analysis</Header1>
      <DashboardLayout
        blockSpecs={[
          blockSpecsMap[FEATURE_IMPORTANCE_BLOCK_TITLE],
          blockSpecsMap[FEATURE_CONTRIBUTION_BLOCK_TITLE],
        ]}
      />
    </>
  );

  const dataPointDrilldownSection = (
    <>
      <Header1 className={css(styles.header)}>Data point drilldown</Header1>
      <DashboardLayout blockSpecs={[blockSpecsMap[PREDICTIONS_AND_LABELS_BLOCK_TITLE]]} />
      <div className={css(styles.dropdownSelector)}>
        <div className="u-small-spacing-right">
          <FieldLabel label="Data point:" />
        </div>

        <div className="u-flex-grow">
          <SelectDropdown
            displayName={resolveDataPointId(selectedDataPointId) ?? 'Select data point'}
            menu={
              <SearchableMenu
                height={MenuHeight.MEDIUM}
                width={MenuWidth.FULL}
                optionDatas={dataPointIds.map((dataPointId) => ({
                  name: resolveDataPointId(dataPointId),
                  value: dataPointId,
                }))}
                onSelect={(dataPointId) => {
                  setSelectedDataPointId(dataPointId);
                }}
                selectedValue={selectedDataPointId}
              />
            }
          />
        </div>
      </div>
      <DashboardLayout
        blockSpecs={[
          blockSpecsMap[LOCAL_EXPLANATION_BLOCK_TITLE],
          blockSpecsMap[SELECTED_PREDICTION_FEATURES_TITLE],
        ]}
      />
    </>
  );

  return (
    <div className={css(styles.stepWrapper)}>
      {expandedBlock == null ? (
        <>
          {summarySection}
          {parameterAnalysisSection}
          {dataPointDrilldownSection}
        </>
      ) : (
        <DashboardLayout blockSpecs={[blockSpecsMap[expandedBlock]]} expandedBlockKey={expandedBlock} />
      )}
    </div>
  );
}

export default function LegacyQueryingParameterOptimizationModelingDashboardView(
  props: MainViewComponentProps<AnalysisMainViewItemType.PARAMETER_OPTIMIZATION_MODELING_DASHBOARD>
): JSX.Element {
  const {step} = props;
  return (
    <BenchlingQuery<GetParameterOptimizationOutputsQuery, GetParameterOptimizationOutputsQueryVariables>
      query={GET_PARAMETER_OPTIMIZATION_MODELING_DASHBOARD_DATA_FRAMES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        allDataId: findAndAssertSingleStepDataFrameId(step, ALL_DATA_DATA_FRAME_NAME, false),
        featureImportanceId: findAndAssertSingleStepDataFrameId(
          step,
          FEATURE_IMPORTANCE_DATA_FRAME_NAME,
          false
        ),
        modelEvaluationId: findAndAssertSingleStepDataFrameId(step, MODEL_EVALUATION_DATA_FRAME_NAME, false),
        searchRankingsId: findAndAssertSingleStepDataFrameId(step, SEARCH_RANKINGS_DATA_FRAME_NAME, false),
        shapValuesId: findAndAssertSingleStepDataFrameId(step, SHAP_VALUES_DATA_FRAME_NAME, false),
      }}
    >
      {(data) => {
        if (
          isInaccessible(data.allData) ||
          isInaccessible(data.featureImportance) ||
          isInaccessible(data.modelEvaluation) ||
          isInaccessible(data.searchRankings) ||
          isInaccessible(data.shapValues)
        ) {
          assertAndContinue(false, 'Got inaccessible dataframe within an analysis', {
            allDataId: isInaccessible(data.allData) ? data.allData.invalidId : data.allData.id,
            featureImportanceId: isInaccessible(data.featureImportance)
              ? data.featureImportance.invalidId
              : data.featureImportance.id,
            modelEvaluationId: isInaccessible(data.modelEvaluation)
              ? data.modelEvaluation.invalidId
              : data.modelEvaluation.id,
            searchRankingsId: isInaccessible(data.searchRankings)
              ? data.searchRankings.invalidId
              : data.searchRankings.id,
            shapValuesId: isInaccessible(data.shapValues) ? data.shapValues.invalidId : data.shapValues.id,
          });
          return <ErrorBanner>You do not have access to the datasets for this dashboard.</ErrorBanner>;
        } else {
          return (
            <LegacyParameterOptimizationModelingDashboardView
              {...props}
              data={{
                allData: data.allData.dataFrameJson,
                featureImportance: data.featureImportance.dataFrameJson,
                modelEvaluation: data.modelEvaluation.dataFrameJson,
                searchRankings: data.searchRankings.dataFrameJson,
                shapValues: data.shapValues.dataFrameJson,
              }}
            />
          );
        }
      }}
    </BenchlingQuery>
  );
}

const SPACE_BETWEEN_DROPDOWNS = 32;
const DROPDOWN_MAX_WIDTH = 300;

const styles = StyleSheet.create({
  header: {
    marginLeft: ANALYSIS_TOOL_SPACING,
  },
  firstHeader: {
    marginTop: ANALYSIS_TOOL_SPACING,
  },
  dropdownSelector: {
    marginLeft: ANALYSIS_TOOL_SPACING,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginRight: SPACE_BETWEEN_DROPDOWNS,
    width: DROPDOWN_MAX_WIDTH,
  },
  stepWrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});
